import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useGeolocation } from 'src/hooks/geo'
import { Asset } from 'src/types'

// We can't import Leaflet as usual because it breaks on server-side.
// Also, we can't conditionally import, so we have to fall back to require
declare const window: any
declare const __CLIENT__: boolean
const L = __CLIENT__ ? require('leaflet') : null
__CLIENT__ && require('leaflet.markercluster')

const tileUrl =
  process.env.TILE_URL || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'

export const useMap = (id: string) => {
  const history = useHistory()
  const theMap = useRef(null)

  // Map initialization
  useEffect(() => {
    if (theMap.current) return // initialised already?

    theMap.current = L.map(id, {
      boxZoom: false,
      keyboard: false,
      zoomSnap: 1,
      maxZoom: 18,
      minZoom: 5,
      worldCopyJump: true
    })

    if (history.location.state?.center) {
      theMap.current.setView(
        history.location.state.center,
        history.location.state.zoom
      )
    } else {
      const spainBounds = L.latLngBounds(L.latLng(44, -18), L.latLng(27, 4))
      theMap.current.fitBounds(spainBounds)
    }

    new L.TileLayer(tileUrl, {
      maxZoom: 19,
      referrerPolicy: 'strict-origin-when-cross-origin'
    }).addTo(theMap.current)

    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(() => {
        theMap.current.invalidateSize()
      })
      observer.observe(document.getElementById(id))
    }
  }, [history.location.state, id])

  return theMap
}

export const useMapMarkers = (
  theMap: React.MutableRefObject<any>,
  assets: Asset[]
) => {
  const markers = useRef(null)
  const history = useHistory()

  useEffect(() => {
    const icon =
      L &&
      L.divIcon({
        className: 'map-marker-asset',
        iconSize: null
      })

    if (markers.current) {
      theMap.current.removeLayer(markers.current)
    }

    markers.current = L.markerClusterGroup({
      animate: false,
      chunkedLoading: true,
      showCoverageOnHover: false
    })

    assets.forEach((asset) => {
      L.marker([parseFloat(asset?.lat), parseFloat(asset?.lon)], { icon })
        .addTo(markers.current)
        .on('click', () => history.push('/assets/' + asset.slug))
    })

    markers.current.addTo(theMap.current)
  }, [assets, history, theMap])
}

export const useMapSelf = (theMap: React.MutableRefObject<any>) => {
  const loc = useGeolocation(true)
  const marker = useRef(null)
  useEffect(() => {
    if (marker.current) {
      theMap.current.removeLayer(marker.current)
    }
    if (loc?.length) {
      marker.current = L.marker([loc[0], loc[1]]) // TODO: Different marker?
      marker.current.addTo(theMap.current)
    }
  }, [loc, theMap])
}

export const useMapLastPosition = (theMap: React.MutableRefObject<any>) => {
  const history = useHistory()
  useEffect(() => {
    const handler = () => {
      const { lat, lng } = theMap.current.getCenter()
      const zoom = theMap.current.getZoom()
      history.replace(history.location.pathname, {
        ...history?.location?.state,
        center: { lat, lng },
        zoom: zoom
      })
    }
    const copyRef = theMap.current
    copyRef.on('moveend', handler)
    return () => copyRef.off('moveend', handler)
  }, [history, theMap])
}
